
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, onMounted, PropType, reactive, ref, watch } from 'vue'
import { MarketplaceIntegration } from '@/types/marketplace'
import phoneValidator from '@/helpers/validators/phoneValidator'
import store from '@/store/index'
import { createLeadInAmoCrm, LeadType, Column } from '@/api/amocrm'
import config from '@root/config'
import IntegrationRequestResultModal, {
    IntegrationRequestResultModalType,
} from '@/modules/marketplace/components/IntegrationRequestResultModal.vue'

const MARKETPLACE_FORM_STORAGE_KEY = 'marketplaceForm'

type MarketplaceStoredForm = {
    name?: string
    phone?: string
    email?: string
}

type RegisterGuestResult = 'registered' | 'alreadyExists' | 'skipped' | 'failed'
type CreateLeadInAmoCrmParams = Parameters<typeof createLeadInAmoCrm>[0]

export default defineComponent({
    name: 'IntegrationRequestSidebar',
    components: {
        IntegrationRequestResultModal,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        integration: {
            type: Object as PropType<MarketplaceIntegration>,
            required: true,
        },
    },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy as {
            $el?: Element
            $auth?: {
                isAuthenticated?: boolean
                user?: {
                    email?: string
                }
                register?: (
                    email: string,
                    password: string,
                    utmSource: string | null,
                    sendPasswordToEmail?: boolean,
                ) => Promise<{ error?: { message?: string; comment?: string }; data?: { userState?: string } }>
                login?: (
                    email: string,
                    password: string,
                ) => Promise<{ error?: { message?: string } }>
            }
            $route?: {
                query?: Record<string, string | (string | null)[] | null | undefined>
                params?: Record<string, string | undefined>
            }
        }
        const form = reactive({
            name: '',
            phone: '',
            email: '',
        })
        const errors = reactive({
            name: '',
            phone: '',
            email: '',
            consent: '',
        })
        const personalDataConsent = ref(false)
        const isSubmitting = ref(false)
        const resultModalOpened = ref(false)
        const resultModalType = ref<IntegrationRequestResultModalType | null>(null)
        const resultModalEmail = ref('')
        const phoneMaskOptions = ref('+#*')
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
        const nameRegex = /^[A-Za-zА-Яа-яЁё]+(?: [A-Za-zА-Яа-яЁё]+)*$/
        const normalizeName = (name: string) => name.replace(/\s+/g, ' ').trim()
        const isNameValidValue = (name: string) => {
            const normalizedName = normalizeName(name)
            return normalizedName.length >= 2 && normalizedName.length <= 50 && nameRegex.test(normalizedName)
        }

        const sidebarTitle = computed(() => `${props.integration.name} — заявка`)
        const isEmailLocked = computed(() => Boolean(getAuthEmail()))
        const isFormValid = computed(() => {
            return (
                isNameValidValue(form.name) &&
                !phoneValidator(form.phone) &&
                emailRegex.test(form.email.trim()) &&
                personalDataConsent.value
            )
        })

        const integrationIcon = computed(() => {
            const name = props.integration.name.toLowerCase()
            if (name.includes('обуч') || name.includes('интенсив')) return '🎓'
            if (name.includes('kpi') || name.includes('показател')) return '📊'
            if (name.includes('то crm')) return '🔧'
            if (name.includes('crm') || name.includes('срм')) return '👑'
            if (name.includes('автопилот')) return '💄'
            if (name.includes('аудиорассылки') || name.includes('звонобот')) return '📞'
            if (name.includes('мультичат')) return '💬'
            if (name.includes('горячие окна')) return '🔥'
            if (name.includes('диагностика')) return '🔍'
            if (name.includes('каскад') || name.includes('отчёт 2.0')) return '📈'  
            if (name.includes('опз 2.0')) return '🔁'
            if (name.includes('sms')) return '📱'
            if (name.includes('вебинары')) return '🎓'
            return '📦'
        })
        const integrationBcg = computed(() => {
            const name = props.integration.name.toLowerCase()
            if (name.includes('обуч') || name.includes('интенсив')) return 'linear-gradient(135deg, #525AD5 0%, #3B41A8 100%)'
            if (name.includes('kpi') || name.includes('показател')) return 'linear-gradient(135deg, #15803D 0%, #0D5C2E 100%)'
            if (name.includes('crm') || name.includes('срм')) return 'linear-gradient(135deg, #C9A227 0%, #A07B0B 100%)'
            if (name.includes('автопилот')) return 'linear-gradient(135deg, #FF6B9D 0%, #C44FA0 100%)'
            return 'linear-gradient(135deg, #525AD5 0%, #3B41A8 100%)'
        })

        const closeResultModal = () => {
            resultModalOpened.value = false
            resultModalType.value = null
            resultModalEmail.value = ''
        }

        const openResultModal = (type: IntegrationRequestResultModalType, email = '') => {
            resultModalType.value = type
            resultModalEmail.value = email
            resultModalOpened.value = true
        }

        const onResultModalInput = (isOpened: boolean) => {
            if (!isOpened) {
                closeResultModal()
                return
            }
            resultModalOpened.value = true
        }

        const showSubmitResult = (registerResult: RegisterGuestResult) => {
            if (registerResult === 'registered') {
                openResultModal('registered', form.email.trim())
                return
            }
            if (registerResult === 'alreadyExists') {
                openResultModal('alreadyExists', form.email.trim())
                return
            }
            openResultModal('sent')
        }

        const close = () => {
            if (isSubmitting.value) return
            emit('input', false)
        }

        const clearErrors = () => {
            errors.name = ''
            errors.phone = ''
            errors.email = ''
            errors.consent = ''
        }

        const getAuthEmail = () => {
            if (!root.$auth?.isAuthenticated) return ''
            return root.$auth.user?.email || ''
        }

        const getStoredForm = (): MarketplaceStoredForm => {
            try {
                return JSON.parse(localStorage.getItem(MARKETPLACE_FORM_STORAGE_KEY) || '{}')
            } catch (e) {
                return {}
            }
        }

        const getUtmMarks = () => {
            const utmMarks: Record<string, string> = {}
            const addUtmMark = (key: string, value?: string | (string | null)[] | null) => {
                if (!key.startsWith('utm_')) return
                const normalizedValue = Array.isArray(value) ? value.find(Boolean) : value
                if (!normalizedValue) return

                utmMarks[key] = normalizedValue
                localStorage.setItem(key, normalizedValue)
            }

            Object.entries(root.$route?.query || {}).forEach(([key, value]) => addUtmMark(key, value))

            for (let index = 0; index < localStorage.length; index += 1) {
                const key = localStorage.key(index)
                if (!key?.startsWith('utm_') || utmMarks[key]) continue
                const value = localStorage.getItem(key)
                if (value) utmMarks[key] = value
            }

            return utmMarks
        }

        const clearUtmMarks = () => {
            try {
                const utmKeys: string[] = []
                for (let index = 0; index < localStorage.length; index += 1) {
                    const key = localStorage.key(index)
                    if (key?.startsWith('utm_')) {
                        utmKeys.push(key)
                    }
                }
                utmKeys.forEach(key => localStorage.removeItem(key))
            } catch (e) {
                // ignore storage access issues
            }
        }

        const hydrateForm = () => {
            const storedForm = getStoredForm()
            const authEmail = getAuthEmail()

            form.name = storedForm.name || ''
            form.phone = storedForm.phone || ''
            form.email = authEmail || storedForm.email || ''
            personalDataConsent.value = false
            changePhoneMask(form.phone.replace(/[^\d+]/g, ''))
            clearErrors()
        }

        const saveForm = () => {
            localStorage.setItem(
                MARKETPLACE_FORM_STORAGE_KEY,
                JSON.stringify({
                    name: normalizeName(form.name),
                    phone: form.phone.replace(/[^\d+]/g, ''),
                    email: form.email.trim(),
                }),
            )
        }

        const changePhoneMask = (phone: string) => {
            const cleanPhone = phone?.startsWith('+') ? phone : `+${phone}`
            if (cleanPhone?.startsWith('+998') || cleanPhone?.startsWith('+971')) {
                phoneMaskOptions.value = '+###-##-#*'
            } else if (cleanPhone?.startsWith('+996')) {
                phoneMaskOptions.value = '+### ### #*'
            } else if (cleanPhone?.startsWith('+995')) {
                phoneMaskOptions.value = '+### (###) ###-#*'
            } else if (cleanPhone?.startsWith('+994')) {
                phoneMaskOptions.value = '+### ### ### ## #*'
            } else if (cleanPhone?.startsWith('+993')) {
                phoneMaskOptions.value = '+###-#-###-#*'
            } else if (cleanPhone?.startsWith('+992')) {
                phoneMaskOptions.value = '+### ## ### ## #*'
            } else if (cleanPhone?.startsWith('+380')) {
                phoneMaskOptions.value = '+### ## #*'
            } else if (cleanPhone?.startsWith('+375')) {
                phoneMaskOptions.value = '+### ## ###-##-#*'
            } else if (cleanPhone?.startsWith('+374')) {
                phoneMaskOptions.value = '+###-##-###-#*'
            } else if (cleanPhone?.startsWith('+373')) {
                phoneMaskOptions.value = '+###-####-#*'
            } else if (cleanPhone?.startsWith('+66')) {
                phoneMaskOptions.value = '+##-##-###-##-#*'
            } else if (cleanPhone?.startsWith('+62')) {
                phoneMaskOptions.value = '+## ## ### ### #*'
            } else if (cleanPhone?.startsWith('+44')) {
                phoneMaskOptions.value = '+## #### #*'
            } else if (cleanPhone?.startsWith('+7') || cleanPhone?.startsWith('+8')) {
                phoneMaskOptions.value = '+# (###) ###-##-#*'
            } else if (cleanPhone?.startsWith('+1')) {
                phoneMaskOptions.value = '+# (###) ###-#*'
            } else {
                phoneMaskOptions.value = '+#*'
            }
        }

        const validateName = () => {
            const name = normalizeName(form.name)
            if (!name) {
                errors.name = 'Поле должно быть заполнено'
            } else if (!isNameValidValue(name)) {
                errors.name = 'Имя должно содержать только буквы и пробелы, от 2 до 50 символов'
            } else {
                errors.name = ''
            }
            return !errors.name
        }

        const validatePhone = () => {
            errors.phone = phoneValidator(form.phone) || ''
            return !errors.phone
        }

        const validateEmail = () => {
            const email = form.email.trim()
            if (!email) {
                errors.email = 'Поле должно быть заполнено'
            } else if (!emailRegex.test(email)) {
                errors.email = 'Некорректный e-mail'
            } else {
                errors.email = ''
            }
            return !errors.email
        }

        const validateConsent = () => {
            errors.consent = personalDataConsent.value
                ? ''
                : 'Подтвердите согласие на обработку персональных данных.'
            return !errors.consent
        }

        const validateForm = () => {
            const isNameValid = validateName()
            const isPhoneValid = validatePhone()
            const isEmailValid = validateEmail()
            const isConsentValid = validateConsent()
            return isNameValid && isPhoneValid && isEmailValid && isConsentValid
        }

        const onNameInput = (event: Event) => {
            const target = event.target as HTMLInputElement
            form.name = target.value
                .replace(/[^A-Za-zА-Яа-яЁё\s]/g, '')
                .replace(/\s+/g, ' ')
                .replace(/^\s/, '')
                .slice(0, 50)
            errors.name = ''
        }

        const onPhoneInput = (event: Event) => {
            const target = event.target as HTMLInputElement
            form.phone = target.value.replace('+8', '+7')
            changePhoneMask(form.phone.replace(/[^\d+]/g, ''))
            errors.phone = ''
        }

        const generatePassword = (length = 8) => {
            const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
            const lower = 'abcdefghijklmnopqrstuvwxyz'
            const digits = '0123456789'
            const all = upper + lower + digits
            const pick = (chars: string) => chars[Math.floor(Math.random() * chars.length)]
            const chars = [pick(upper), pick(lower), pick(digits)]

            while (chars.length < length) {
                chars.push(pick(all))
            }

            for (let i = chars.length - 1; i > 0; i -= 1) {
                const j = Math.floor(Math.random() * (i + 1))
                ;[chars[i], chars[j]] = [chars[j], chars[i]]
            }

            return chars.join('')
        }

        const isUserAlreadyRegistered = (res: {
            error?: { message?: string; comment?: string }
            data?: { userState?: string }
        }) => {
            if (res.data?.userState === 'alreadyExists') return true
            const message = String(res.error?.message || res.error?.comment || '').toLowerCase()
            return /already|exist|существ|зарегистр|занят/.test(message)
        }

        const registerGuestUser = async (): Promise<RegisterGuestResult> => {
            if (root.$auth?.isAuthenticated || !root.$auth?.register || !root.$auth?.login) {
                return 'skipped'
            }

            const email = form.email.trim()
            const password = generatePassword()
            const utmSource = localStorage.getItem('utm_source')

            const registerResponse = await root.$auth.register(
                email,
                password,
                utmSource,
                true,
            )
            if (registerResponse.error) {
                return isUserAlreadyRegistered(registerResponse) ? 'alreadyExists' : 'failed'
            }

            const { error: loginError } = await root.$auth.login(email, password)
            return loginError ? 'failed' : 'registered'
        }

        const submit = async () => {
            if (isSubmitting.value) return
            if (!validateForm()) return
            isSubmitting.value = true
            const currentIntegrationType = Object.entries(
                config.marketplaceModulesIds[config.buildType],
            ).find(([, integrationId]) => integrationId === props.integration.id)?.[0]
            const LeadAndNoteMap: Record<string, [LeadType, Column, string, string, string]> = {
                beautyAutopilot: ['Бьюти-автопилот', 'Новая заявка', 'Заявка с МП. Бьюти-автопилот', null, 'form0061'],
                salesTraining: ['Агентство (лиды)', 'Новая сделка', 'Заявка с МП. Интенсив', null, 'form0062'],
                crmSettings: ['Агентство (лиды)', 'Новая сделка', 'Заявка с МП. Настройка CRM', null, 'form0063'],
                kpiSettings: ['Агентство (лиды)', 'Новая сделка', 'Заявка с МП. Настройка KPI', null, 'form0062' ],
                maxBusiness: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. MAX Business', null, 'form0064'],
                channels: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. Новые каналы', null, 'form0065'],
                crmTO: ['Агентство (лиды)', 'Новая сделка', 'Заявка с МП. CRM TO', null, 'form0066'],
                zvonoBot: ['Агентство (лиды)', 'Новая сделка', 'Заявка с МП. Аудиорассылки DH', null, 'form0067'],
                dhChat: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. Мультичат', null, 'form0068'],
                diagnostics: ['Агентство (лиды)', 'Новая сделка', 'Заявка с МП. Диагностика', null, 'form0069'],
                customCascades: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. Дополнительные каскады', null, 'form0070'],
                sms: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. SMS DH', null, 'form0071'],
                businessPulseReport: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. Отчёт 2.0', null, 'form0072'],
                opz2: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. ОПЗ 2.0', null, 'form0073'],
                hotWindows: ['Лидоруб', 'НОВЫЕ, дх', 'Новый лид. Заявка с МП. Горячие окна', null, 'form0074'],
                webinars: ['Агентство (Вебинарная)', 'новые', 'Заявка с МП. Вебинары', null, 'form0075'],
                opz2KPISettings: ['Внедрение', 'Ожидаем оплату', 'Заявка МП на настройку ОПЗ 2.0', null, 'form0076'],
                zvonoBotSettings: ['Агентство (лиды)', 'Новая сделка', 'Заявка МП на настройку Аудиорассылки DH', null, 'form0077'],
            }
            const [type, column, rawRequestName, note, formId] = LeadAndNoteMap[currentIntegrationType]
            const utm = getUtmMarks()
            const url = typeof window !== 'undefined' ? window.location.href : ''
            const projectId = root.$route?.params?.id
            const requestName =
                currentIntegrationType === 'beautyAutopilot' && !projectId
                    ? 'Нет проекта. Заявка с МП. Бьюти-автопилот.'
                    : rawRequestName
            const params: CreateLeadInAmoCrmParams = {
                name: normalizeName(form.name),
                phone: form.phone.replace(/[^\d+]/g, ''),
                email: form.email.trim(),
                type,
                note,
                column,
                requestName,
                formId,
                integrationId: props.integration.id,
                ...(projectId ? { projectId } : {}),
                utm,
                url,
            }
            console.log('currentIntegrationType', currentIntegrationType)
            console.log('integration request form', {
                integrationId: props.integration.id,
                integrationName: props.integration.name,
                name: normalizeName(form.name),
                phone: form.phone.replace(/[^\d+]/g, ''),
                email: form.email.trim(),
                utm,
            })
            saveForm()
            let isLeadSubmitted = false
            let registerResult: RegisterGuestResult = 'skipped'
            try {
                await createLeadInAmoCrm(params)
                isLeadSubmitted = true
                try {
                    registerResult = await registerGuestUser()
                } catch {
                    registerResult = 'failed'
                }
            } catch (error) {
                store.dispatch(
                    'callNotify',
                    'Ошибка при отправке заявки. Попробуйте позже.',
                )
            } finally {
                isSubmitting.value = false
                if (isLeadSubmitted) {
                    clearUtmMarks()
                    emit('input', false)
                    showSubmitResult(registerResult)
                }
            }
        }

        const mountSidebarToBody = () => {
            const el = root.$el
            if (el && el.parentNode !== document.body) {
                document.body.appendChild(el)
            }
        }

        onMounted(mountSidebarToBody)
        onBeforeUnmount(() => {
            const el = root.$el
            if (el?.parentNode === document.body) {
                document.body.removeChild(el)
            }
        })

        watch(
            () => props.value,
            (isOpened) => {
                if (isOpened) hydrateForm()
            },
        )

        return {
            form,
            errors,
            personalDataConsent,
            isSubmitting,
            isFormValid,
            isEmailLocked,
            phoneMaskOptions,
            sidebarTitle,
            integrationIcon,
            integrationBcg,
            resultModalOpened,
            resultModalType,
            resultModalEmail,
            close,
            onResultModalInput,
            submit,
            onNameInput,
            onPhoneInput,
            validateName,
            validatePhone,
            validateEmail,
        }
    },
})
